import { useShowcaseLanguage } from "./useShowcaseLanguage";
import { useEffect, useRef, useState } from "react";
import { MacIcon } from "src/shared/ui/src/design/MacIcon";
import "./competition-story.css";

const duration = 32;
const captions = [
  "Attendez l’arbitrage de la barre annoncée à 195 kg. Le prochain essai est encore vide.",
  "Les trois décisions des arbitres valident automatiquement la barre à 195 kg.",
  "Survolez le badge A pour consulter les décisions des arbitres.",
  "Après le passage, saisissez le prochain essai de cet athlète : 202,5 kg.",
  "Le troisième essai est annoncé. Le deuxième tour continue avec l’athlète suivant.",
  "L’essai de Maël AUBERVAL à 197,5 kg est refusé automatiquement.",
  "Consultez les deux décisions rouges et la décision blanche dans l’infobulle.",
];
const points = [
  [0, 55, 48],
  [1.5, 57, 45.5],
  [3.7, 61.4, 39.4],
  [5.7, 63.6, 37.2],
  [9.6, 63.6, 37.2],
  [10.95, 69.3, 35.8],
  [11.25, 69.3, 35.8],
  [12, 74, 41],
  [14.7, 74, 41],
  [16.3, 72, 43.8],
  [19.1, 63.6, 42.1],
  [24.3, 63.6, 42.1],
  [27, 77, 48],
  [32, 77, 48],
];
function pointer(time: number) {
  const next = points.findIndex((point) => point[0] > time);
  if (next < 1) return points[points.length - 1].slice(1);
  const a = points[next - 1],
    b = points[next];
  const dt = b[0] - a[0],
    t = (time - a[0]) / dt;
  const stationary = (p: number[], q: number[]) =>
    p[1] === q[1] && p[2] === q[2];
  if (stationary(a, b)) return a.slice(1);
  const before = points[Math.max(0, next - 2)],
    after = points[Math.min(points.length - 1, next + 1)];
  return [1, 2].map((axis) => {
    const m0 = stationary(before, a)
      ? 0
      : (b[axis] - before[axis]) / (b[0] - before[0]);
    const m1 = stationary(b, after)
      ? 0
      : (after[axis] - a[axis]) / (after[0] - a[0]);
    return (
      (2 * t * t * t - 3 * t * t + 1) * a[axis] +
      (t * t * t - 2 * t * t + t) * dt * m0 +
      (-2 * t * t * t + 3 * t * t) * b[axis] +
      (t * t * t - t * t) * dt * m1
    );
  });
}
const smooth = (value: number) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};

/** Real Electron captures: charge entry, local referee decisions and badge tooltip. */
export default function CompetitionStory({
  theme,
}: {
  theme: "light" | "dark";
}) {
  const { t, assetBase, language } = useShowcaseLanguage();
  const root = useRef<HTMLElement>(null);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      setPlaying(!motion.matches);
      if (motion.matches) setTime(8);
    };
    update();
    motion.addEventListener("change", update);
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.15 },
    );
    if (root.current) observer.observe(root.current);
    return () => {
      observer.disconnect();
      motion.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setFailed(false);
    Promise.all(
      Array.from(
        { length: 12 },
        (_, frame) =>
          new Promise<void>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = reject;
            image.src = `${assetBase}competition-story/${theme}-${frame}.jpg`;
          }),
      ),
    )
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [theme, assetBase]);

  useEffect(() => {
    if (!ready || !playing || !visible) return;
    let previous = performance.now();
    let animationFrame = 0;
    const tick = (now: number) => {
      const elapsed = (now - previous) / 1000;
      previous = now;
      if (!document.hidden)
        setTime((value) => (value + Math.min(elapsed, 0.1)) % duration);
      animationFrame = requestAnimationFrame(tick);
    };
    animationFrame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrame);
  }, [ready, playing, visible]);

  const frame =
    time < 4
      ? 0
      : time < 6.2
        ? 1
        : time < 10.9
          ? 2
          : time < 12
            ? 3
            : time < 12.3
              ? 4
              : time < 12.65
                ? 5
                : time < 12.9
                  ? 6
                  : time < 13.2
                    ? 7
                    : time < 14.5
                      ? 8
                      : time < 17.4
                        ? 9
                        : time < 19.3
                          ? 10
                          : 11;
  const chapter =
    frame < 3 ? frame : frame < 9 ? 3 : frame === 9 ? 4 : frame - 5;
  const zoom = 1 + 1.7 * smooth((time - 1) / 2) * (1 - smooth((time - 28) / 2));
  const [pointerX, pointerY] = pointer(time);
  const x = pointerX - (language === "en" ? 0.2 : 0);
  const y = pointerY + (language === "en" ? 0.65 : 0);
  return (
    <figure
      className="competition-story"
      ref={root}
      aria-label={t(
        "Démonstration réelle de la saisie et de l’arbitrage automatique",
      )}
    >
      <div className="competition-stage" aria-hidden="true">
        <div
          className="competition-canvas"
          style={{ transform: `scale(${ready ? zoom : 1})` }}
        >
          <img
            src={`${assetBase}competition-story/${theme}-${ready ? frame : 0}.jpg`}
            width="1512"
            height="920"
            alt=""
          />
          {ready && (
            <svg
              className="competition-pointer"
              viewBox="0 0 24 30"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <path
                d="m2 2 19 15-10 1-4 9Z"
                fill="white"
                stroke="#202124"
                strokeWidth="1.5"
              />
            </svg>
          )}
          {ready && time >= 10.9 && time < 11.35 && (
            <span
              className="competition-click"
              style={{
                left: language === "en" ? "69.1%" : "69.3%",
                top: language === "en" ? "36.45%" : "35.8%",
                opacity: 1 - (time - 10.9) / 0.45,
              }}
            />
          )}
        </div>
      </div>
      <div className="planning-controls">
        <button
          type="button"
          disabled={!ready}
          onClick={() => setPlaying(!playing)}
          aria-label={
            playing
              ? t("Mettre en pause l’arbitrage")
              : t("Lire l’animation de l’arbitrage")
          }
        >
          <MacIcon name={playing ? "pause" : "play"} size={15} />
        </button>
        <button
          type="button"
          disabled={!ready}
          onClick={() => {
            setTime(0);
            setPlaying(true);
          }}
          aria-label={t("Rejouer l’arbitrage")}
        >
          <MacIcon name="undo" size={14} />
        </button>
        <input
          aria-label={t("Position dans l’animation de l’arbitrage")}
          type="range"
          min="0"
          max={duration - 0.1}
          step="0.1"
          value={time}
          onChange={(event) => {
            setPlaying(false);
            setTime(Number(event.target.value));
          }}
        />
        <span>{`${String(Math.floor(time)).padStart(2, "0")} / ${duration} s`}</span>
      </div>
      <figcaption className="planning-source" role="status">
        {failed
          ? t(
              "Aperçu indisponible. Rechargez la page pour lire la démonstration.",
            )
          : t(captions[chapter])}
        {t(" Application réelle, données de démonstration.")}
      </figcaption>
    </figure>
  );
}
